import { basename, join } from 'path';
import { readFile as readFileFs, readdir, stat, exists } from 'fs';
import type { Manifest, FileData } from '@sap/ux-specification-types';

export interface Project {
    root: string;
    name: string;
    manifest: Manifest;
    annotations: FileData[];
    changes: string[];
    fragments: FileData[];
    views: FileData[];
}

const readFile = async (path: string): Promise<string> => {
    return new Promise((resolve, reject): void => {
        readFileFs(path, { encoding: 'utf8' }, (err, data): void => {
            if (err) {
                reject(err);
            } else {
                resolve(data);
            }
        });
    });
};

const readJSON = async <T>(path: string): Promise<T> => {
    return JSON.parse(await readFile(path)) as T;
};

export const readDirectory = async (path: string): Promise<string[]> => {
    return new Promise((resolve, reject): void => {
        readdir(path, { encoding: 'utf8' }, (err, data): void => {
            if (err) {
                reject(err);
            } else {
                resolve(data);
            }
        });
    });
};

export const isDirectory = async (path: string): Promise<boolean> => {
    return new Promise((resolve): void => {
        stat(path, (err, stats): void => {
            if (err) {
                resolve(false);
            } else {
                resolve(stats.isDirectory());
            }
        });
    });
};

const resourceExists = async (path: string): Promise<boolean> => {
    return new Promise((resolve) => {
        exists(path, (doesExist) => {
            resolve(doesExist);
        });
    });
};

const getManifest = async (path: string): Promise<Manifest> => {
    return readJSON<Manifest>(join(path, 'manifest.json'));
};

export const getChanges = async (webapp: string): Promise<string[]> => {
    const changes: string[] = [];
    const changesDirectory = join(webapp, 'changes');
    if (!(await resourceExists(changesDirectory))) {
        return changes;
    }
    const extensions = [
        '.change',
        '.variant',
        '.ctrl_variant',
        '.ctrl_variant_change',
        '.ctrl_variant_management_change'
    ];
    const files = await readDirectory(changesDirectory);
    for (const file of files) {
        if (extensions.some((ext) => file.endsWith(ext))) {
            changes.push(await readFile(join(changesDirectory, file)));
        }
    }
    return changes;
};

const getExtensionFiles = async (webAppPath: string, folder = 'fragments'): Promise<FileData[]> => {
    const fragments: FileData[] = [];

    const directoryPath = join(webAppPath, 'ext', folder);
    if (!(await resourceExists(directoryPath))) {
        return fragments;
    }
    const files = await readDirectory(directoryPath);
    for (const file of files) {
        const filePath = join(directoryPath, file);
        const data = await readFile(filePath);
        fragments.push({
            dataSourceUri: filePath,
            fileContent: data
        });
    }
    return fragments;
};

const getAnnotations = async (manifest: Manifest, webAppPath: string): Promise<FileData[]> => {
    const annotationData: FileData[] = [];
    const serviceDataSources = manifest?.['sap.app']?.dataSources;
    if (serviceDataSources?.mainService?.settings?.localUri) {
        const metadataPath = join(webAppPath, serviceDataSources.mainService.settings.localUri);
        annotationData.push({
            dataSourceUri: metadataPath,
            fileContent: await readFile(metadataPath)
        });
    }
    if (serviceDataSources?.mainService?.settings?.annotations) {
        for (const annotationRef of serviceDataSources.mainService.settings.annotations) {
            if (serviceDataSources[annotationRef]?.settings?.localUri) {
                const annotationFilePath = join(webAppPath, serviceDataSources[annotationRef].settings.localUri);
                annotationData.push({
                    dataSourceUri: annotationFilePath,
                    fileContent: await readFile(annotationFilePath)
                });
            }
        }
    }
    //Consider CAP/multiple services
    if (annotationData.length === 0) {
        for (const key in serviceDataSources) {
            const dataSource = serviceDataSources[key];
            if (dataSource?.type === 'ODataAnnotation' && dataSource?.settings?.localUri) {
                const annotationFilePath = join(webAppPath, dataSource.settings.localUri);
                annotationData.push({
                    dataSourceUri: annotationFilePath,
                    fileContent: await readFile(annotationFilePath)
                });
            }
        }
    }

    return annotationData;
};

/**
 * Get the project artifacts, like manifest, service metadata, annotations.
 *
 * @param root - project root where package.json is
 * @returns A promise that resolves to the project artifacts.
 */
export const getProject = async (root: string): Promise<Project> => {
    const name = basename(root);
    const webappPath = join(root, 'webapp');
    const manifest = await getManifest(webappPath);
    const annotations = await getAnnotations(manifest, webappPath);
    const changes = await getChanges(webappPath);
    const fragments = await getExtensionFiles(webappPath);
    const views = await getExtensionFiles(webappPath, 'views');
    return {
        root,
        name,
        manifest,
        annotations,
        changes,
        fragments,
        views
    };
};
